import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Tooltip } from "@mui/material"; import React, { useState } from "react";
import moment from "moment";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatCurrencyAmount } from "../../../utils/helper";
import { NoRecordFound } from "../../../shared_elements";

const CLOSING_BALANCE_DETAIL_ICON_FLAGS = 'opening_balance_present';

const COLORS = {
    navy: '#1a3a5c',
    navyDark: '#0f2a45',
    blueLink: '#1a5fa8',
    greenSection: '#4CAF50',
    amberSection: '#c9a84c',
    redText: '#c0392b',
    bgTableHeader: '#e4ecf5',
    bgRowAlt: '#f7fafd',
    bgOpening: '#d6e4f0',
    bgOpeningSub: '#eef4fa',
    bgTotal: '#e8f0fb',
    bgClosing: '#1a3a5c',
    bgClosingSub: '#1e3f5a',
    bgIncomeSub: '#eaf4ea',
    bgOutflowSub: '#f7f1de',
    yearTotal: '#c8dff5',
    yearTotalClosing: '#0f2a45',
    border: '#d0dae8',
    cellBorder: '#e4eaf2',
    textPrimary: '#1a2a3a',
    textSecondary: '#5a6a7a',
    textMuted: '#9aabb8',
    closingText: '#cbd8e6',
    closingLabel: '#a8c8e8',
    newTag: '#2e8b7a',
};

const BALANCE_BREAKDOWN_KEYS = ['breakdown', 'balances', 'funds', 'categories', 'sub_categories', 'subcategories', 'transactions', 'details', 'items'];

const getBalanceBreakdown = (row) => {
    if (!row) return [];
    for (const key of BALANCE_BREAKDOWN_KEYS) {
        const list = row[key];
        if (Array.isArray(list) && list.length) return list;
    }
    return [];
};
 
const getBreakdownLabel = (item) =>  item?.name  || '--';

const isNewLineItem = (item) => Boolean(item?.is_new || item?.is_new_line_item || item?.is_new_transaction_type);

const periodRangeFromLabel = (label, mode) => {
    if (!label || typeof label !== "string") return null;
    const trimmed = label.trim();
    const monthAbb = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const m = String(mode || "").toLowerCase();

    if (m === "monthly") {
        const monthIdx = monthAbb.findIndex((abbr) => trimmed.toLowerCase().startsWith(abbr));
        const yearMatch = trimmed.match(/(\d{4})/);
        if (monthIdx === -1 || !yearMatch) return null;
        const y = parseInt(yearMatch[1], 10);
        const start = moment({ year: y, month: monthIdx, day: 1 });
        const end = start.clone().endOf("month");
        return { start_date: start.format("YYYY-MM-DD"), end_date: end.format("YYYY-MM-DD") };
    }

    if (m === "quarterly") {
        const qMatch = trimmed.match(/Q(\d)/i);
        const yearMatch = trimmed.match(/(\d{4})/);
        if (!qMatch || !yearMatch) return null;
        const q = parseInt(qMatch[1], 10);
        if (q < 1 || q > 4) return null;
        const y = parseInt(yearMatch[1], 10);
        const startMonth = (q - 1) * 3;
        const start = moment({ year: y, month: startMonth, day: 1 });
        const end = start.clone().add(2, "months").endOf("month");
        return { start_date: start.format("YYYY-MM-DD"), end_date: end.format("YYYY-MM-DD") };
    }

    if (m === "yearly") {
        let y = null;
        const strictYear = trimmed.match(/^(\d{4})$/);
        if (strictYear) {
            y = parseInt(strictYear[1], 10);
        } else {
            const yearMatch = trimmed.match(/\b(19|20)\d{2}\b/);
            if (yearMatch) y = parseInt(yearMatch[0], 10);
        }
        if (y == null || Number.isNaN(y)) return null;
        const start = moment({ year: y, month: 0, day: 1 });
        const end = start.clone().endOf("year");
        return { start_date: start.format("YYYY-MM-DD"), end_date: end.format("YYYY-MM-DD") };
    }

    return null;
};

const CashFlowListing = ({ cashFlowListing, selectedCurrency, currencySymbol, onOpenDetails, handleItemClicked }) => {
    const [expandedBalance, setExpandedBalance] = useState({ opening: false, closing: false });
    const cellStyle = { border: `1px solid ${COLORS.cellBorder}`, textAlign: "right", padding: "5px 10px", fontSize: "14px", color: COLORS.textPrimary, whiteSpace: "nowrap" };
    const leftCell = { ...cellStyle, textAlign: "left", fontSize: "14px" };
    const dimCell = { textAlign: "center", color: COLORS.textMuted };
    const yearTotalCell = { ...cellStyle, backgroundColor: COLORS.yearTotal, color: COLORS.navy, fontWeight: 600 };
    const toggleStyle = { display: "inline-block", width: "14px", height: "14px", lineHeight: "14px", textAlign: "center", fontSize: "10px", border: `1px solid #8ab0cc`, borderRadius: "3px", marginRight: "6px", color: COLORS.navy, backgroundColor: "#fff", verticalAlign: "middle" };
    const closingToggleStyle = { ...toggleStyle, border: `1px solid #4a6a8a`, color: "#fff", backgroundColor: "rgba(255,255,255,0.15)" };
    const monthAbbrs = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const quarterStartMonths = { 1: 0, 2: 3, 3: 6, 4: 9 };
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const isUpToCurrent = (labelIdx) => {
        const label = cashFlowListing?.labels?.[labelIdx];
        if (!label) return false;

        const yearMatch = label.match(/(\d{4})/);
        if (yearMatch && parseInt(yearMatch[1]) < currentYear) return true;

        const quarterMatch = label.match(/Q(\d)/i);
        if (quarterMatch) {
            const quarter = parseInt(quarterMatch[1]);
            const startMonth = quarterStartMonths[quarter];
            return startMonth !== undefined && currentMonth >= startMonth;
        }

        const monthIdx = monthAbbrs.findIndex(m => label.toLowerCase().startsWith(m));
        if (monthIdx === -1) return true;
        return monthIdx <= currentMonth;
    };
    const displayValue = (val, labelIdx) => isUpToCurrent(labelIdx) ? formatCurrencyAmount(val, selectedCurrency) : "";
    const beginningBalance = cashFlowListing?.data?.find((d) => d.type === "BAL");
    const sections = cashFlowListing?.data?.filter((d) => d.type !== "BAL");
    const closingBalance = cashFlowListing?.data?.find((d) => d.category === "CLOSING BALANCE");

    const listingMode = String(cashFlowListing?.mode || "").toLowerCase();

    const openingBreakdown = getBalanceBreakdown(beginningBalance);
    const closingBreakdown = getBalanceBreakdown(closingBalance);

    const toggleBalance = (key) => {
        setExpandedBalance((prev) => ({ ...prev, [key]: !prev[key] }));
        handleItemClicked(`${expandedBalance[key] ? 'Collapsed' : 'Expanded'} ${key === 'opening' ? 'Opening' : 'Closing'} Balance`);
    };

    const shouldShowClosingDetailIcon = (columnIdx) => {
        if (!onOpenDetails || !closingBalance?.values) return false;
        const flags = closingBalance?.[CLOSING_BALANCE_DETAIL_ICON_FLAGS];
        if (flags == null || !Array.isArray(flags)) return true;
        return Boolean(flags[columnIdx]);
    };

    const handleClosingDetailsClick = (labelIdx) => {
        handleItemClicked('Cashflow Details Icon');
        const label = cashFlowListing?.labels?.[labelIdx];
        const range = periodRangeFromLabel(label, listingMode);
        if (!range || !onOpenDetails) return;
        onOpenDetails({
            start_date: range.start_date,
            end_date: range.end_date,
            mode: listingMode || "monthly",
        });
    };

    // Renders a period value cell: dims empty/future periods and colours outflow amounts red
    const renderValueCell = (val, idx, { negative = false, sx = {} } = {}) => {
        const text = displayValue(val, idx);
        return (
            <TableCell key={idx} sx={{ ...cellStyle, ...sx, ...(text ? {} : dimCell), ...(text && negative ? { color: COLORS.redText } : {}) }}>
                {text || '-'}
            </TableCell>
        );
    };

    const renderBalanceSubRows = (items, variant) => {
        
        const isClosing = variant === 'closing';
        const rowSx = { backgroundColor: isClosing ? COLORS.bgClosingSub : COLORS.bgOpeningSub };
        const valueSx = { fontWeight: 400, ...(isClosing ? { color: COLORS.closingText } : {}) };
        if (!items.length) {
            return (
                <TableRow sx={rowSx}>
                    <TableCell colSpan={(cashFlowListing?.labels?.length || 0) + 2} sx={{ ...leftCell, paddingLeft: "32px", fontSize: "14px", fontWeight: 400, fontStyle: 'italic', color: isClosing ? COLORS.closingLabel : COLORS.textSecondary }}>
                        No breakdown available
                    </TableCell>
                </TableRow>
            );
        }
        return items?.map((item, idx) => (
            <TableRow key={`${variant}-sub-${idx}`} sx={rowSx}>
                <TableCell sx={{ ...leftCell, paddingLeft: "32px", fontSize: "14px", fontWeight: 400, color: isClosing ? COLORS.closingLabel : COLORS.blueLink }}>
                    {getBreakdownLabel(item)}
                </TableCell>
                {(item?.values || [])?.map((val, i) => renderValueCell(val, i, { sx: valueSx }))}
                <TableCell sx={{ ...yearTotalCell, ...(isClosing ? { backgroundColor: COLORS.yearTotalClosing, color: "#fff" } : {}) }}>
                    {formatCurrencyAmount(item?.year_total, selectedCurrency)}
                </TableCell>
            </TableRow>
        ));
    };

    return (
        <>
            <TableContainer component={Paper} sx={{overflow: 'auto', border: `1px solid ${COLORS.border}`, flex: 1, minHeight: 0}}>
            <Table sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: COLORS.bgTableHeader }}>
                        <TableCell sx={{ ...leftCell, fontWeight: 600, fontSize: '14px', color: COLORS.navy, border: `1px solid ${COLORS.border}`, minWidth:'210px', position: 'sticky', top: 0, zIndex: 3, backgroundColor: COLORS.bgTableHeader }}> Fiscal Year Overview </TableCell>

                        {cashFlowListing?.labels?.map((label) => (
                            <TableCell key={label} sx={{ ...cellStyle, textAlign: 'center', fontWeight: 600, fontSize: '14px', color: COLORS.navy, border: `1px solid ${COLORS.border}`, position: 'sticky', top: 0, zIndex: 3, backgroundColor: COLORS.bgTableHeader }}> {label}<br/><p style={{fontSize:'9px', margin:'0px', fontWeight: 400, color: COLORS.textSecondary}}>{selectedCurrency} {currencySymbol ? `(${currencySymbol})` : ''}</p></TableCell>
                        ))}

                        <TableCell sx={{ ...cellStyle, textAlign: 'center', fontWeight: 600, fontSize: '14px', color: COLORS.navy, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.yearTotal, position: 'sticky', top: 0, zIndex: 3 }} >
                            Year Total
                            <br/><p style={{fontSize:'9px', margin:'0px', fontWeight: 400, color: COLORS.textSecondary}}>{selectedCurrency} {currencySymbol ? `(${currencySymbol})` : ''}</p>
                        </TableCell>
                    </TableRow>
                </TableHead>

                {beginningBalance && (
                    <TableHead>
                        <TableRow
                            onClick={() => toggleBalance('opening')}
                            sx={{ backgroundColor: COLORS.bgOpening, cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: '#c4d9ed' } }}
                        >
                            <TableCell sx={{ ...leftCell, fontWeight: 600,fontSize: '14px', color: '#0f3a5c', backgroundColor: 'inherit' }}>
                                <Box component="span" sx={toggleStyle}>{expandedBalance.opening ? '−' : '+'}</Box>
                                Opening Balance
                            </TableCell>

                            {beginningBalance?.values?.map((val, idx) => (
                                <TableCell key={idx} sx={{ ...cellStyle, fontWeight: 600,fontSize: '14px', color: '#0f3a5c', backgroundColor: 'inherit', ...(displayValue(val, idx) ? {} : dimCell) }}> {displayValue(val, idx)||'–'} </TableCell>
                            ))}

                            <TableCell sx={yearTotalCell} >
                                {formatCurrencyAmount(beginningBalance.year_total, selectedCurrency)}
                            </TableCell>
                        </TableRow>

                        {expandedBalance.opening ? renderBalanceSubRows(openingBreakdown, 'opening') : null}
                    </TableHead>
                )}

                <TableBody>
                    {!sections?.length ? (
                        <TableRow>
                            <TableCell colSpan={(cashFlowListing?.labels?.length || 0) + 2} sx={{ border: `1px solid ${COLORS.cellBorder}`}}>
                                <NoRecordFound />
                            </TableCell>
                        </TableRow>
                    ) : sections?.map((section, sIdx) => (
                        <React.Fragment key={sIdx}>
                            {(section.type === "IN" || section.type === "OUT") &&
                                section.categories?.map((cat, cIdx) => (
                                    <React.Fragment key={cIdx}>
                                        {/* CATEGORY ROW */}
                                        {cat.has_transaction_types || cat.has_transaction_types_sub_category ? <TableRow sx={{ backgroundColor: section.type === "IN" ? COLORS.greenSection : COLORS.amberSection, }} >
                                            <TableCell colSpan={cashFlowListing.labels.length + 2} sx={{ ...leftCell, fontWeight: 600, fontSize: '14px', color: '#fff' }} >
                                                {section.type === "IN" ? "(+)" : "(-)"}{" "}
                                                {cat.name}
                                            </TableCell>
                                        </TableRow> : null}

                                        {/* DIRECT TRANSACTIONS OF CATEGORY */}
                                        {cat.transactions?.map((txn, tIdx) => (
                                            <TableRow key={`direct-${tIdx}`} sx={tIdx % 2 === 0 ? { backgroundColor: COLORS.bgRowAlt } : {}}>
                                                <TableCell sx={{ ...leftCell, paddingLeft: "28px", fontSize: '14px', color: COLORS.textSecondary }} >
                                                    {txn.transaction_type_alias}
                                                    {isNewLineItem(txn) ? (
                                                        <Box component="span" sx={{ display: 'inline-block', backgroundColor: COLORS.newTag, color: '#fff', fontSize: '8.5px', fontWeight: 600, padding: '1px 5px', borderRadius: '6px', marginLeft: '5px', verticalAlign: 'middle' }}>NEW</Box>
                                                    ) : null}
                                                </TableCell>

                                                {txn?.values?.map((val, i) => renderValueCell(val, i, { negative: section.type !== "IN" }))}
                                                <TableCell sx={yearTotalCell} >
                                                    {formatCurrencyAmount(txn?.year_total, selectedCurrency)}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {/* SUBCATEGORIES */}
                                        {cat.subcategories?.map((sub, subIdx) => (
                                            <React.Fragment key={subIdx}>
                                                {/* SUBCATEGORY ROW */}
                                                {sub.name && sub.transactions && (
                                                    <TableRow sx={{ backgroundColor: section.type === "IN" ? COLORS.bgIncomeSub : COLORS.bgOutflowSub, }} >
                                                        <TableCell colSpan={cashFlowListing.labels.length + 2} sx={{ ...leftCell, paddingLeft: "30px", fontWeight: 600, color: section.type === "IN" ? COLORS.navy : COLORS.redText }} >
                                                            {sub.name}
                                                        </TableCell>
                                                    </TableRow>
                                                )}

                                                {/* SUBCATEGORY TRANSACTIONS */}
                                                {sub.transactions?.map((txn, tIdx) => (
                                                    <TableRow key={`subtxn-${tIdx}`} sx={{ backgroundColor: section.type === "IN" ? COLORS.bgIncomeSub : COLORS.bgOutflowSub, }}>
                                                        <TableCell sx={{ ...leftCell, paddingLeft: "50px", fontSize: '14px', color: section.type === "IN" ? COLORS.textSecondary : COLORS.redText }} >
                                                            {txn.transaction_type_alias}
                                                            {isNewLineItem(txn) ? (
                                                                <Box component="span" sx={{ display: 'inline-block', backgroundColor: COLORS.newTag, color: '#fff', fontSize: '8.5px', fontWeight: 600, padding: '1px 5px', borderRadius: '6px', marginLeft: '5px', verticalAlign: 'middle' }}>NEW</Box>
                                                            ) : null}
                                                        </TableCell>

                                                        {txn?.values?.map((val, i) => renderValueCell(val, i, { negative: section.type !== "IN", sx: { backgroundColor: 'inherit' } }))}

                                                        <TableCell sx={yearTotalCell} >
                                                            {formatCurrencyAmount(txn.year_total, selectedCurrency)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </React.Fragment>
                                ))}

                            {/* TOTAL ROW */}
                            {(section.type === "IN" || section.type === "OUT") && (
                                <TableRow sx={{ backgroundColor: COLORS.bgTotal }}>
                                    <TableCell sx={{ ...leftCell, fontWeight: 600, fontSize: '14px', color: COLORS.navy }}> {section.type === "OUT" ? "Total Cash Outflow" : "Total Cash Inflow"}</TableCell>

                                    {section?.values?.map((val, i) => (
                                        <TableCell key={i} sx={{ ...cellStyle, fontWeight: 600, fontSize: '14px', color: COLORS.navy, ...(displayValue(val, i) ? {} : dimCell) }}> {displayValue(val, i)||'–'} </TableCell>
                                    ))}

                                    <TableCell sx={yearTotalCell} >
                                        {formatCurrencyAmount(section.year_total, selectedCurrency)}
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* NET CASHFLOW */}
                            {section.type === "NET" && closingBalance?.values && (
                                <React.Fragment>
                                    <TableRow
                                        onClick={() => toggleBalance('closing')}
                                        sx={{ backgroundColor: COLORS.bgClosing, cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: '#142d47' } }}
                                    >
                                        <TableCell sx={{ ...leftCell, fontWeight: 600, fontSize: '14px', color: '#fff', backgroundColor: 'inherit' }}>
                                            <Box component="span" sx={closingToggleStyle}>{expandedBalance.closing ? '−' : '+'}</Box>
                                            Closing balance
                                        </TableCell>

                                        {closingBalance?.values?.map((val, i) => (
                                            <TableCell key={i} sx={{ ...cellStyle, fontWeight: 600, fontSize: '14px', color: '#fff', backgroundColor: 'inherit' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                                    <span>{displayValue(val, i)||'–'}</span>
                                                    {shouldShowClosingDetailIcon(i) ? (
                                                        <Tooltip title="Cashflow Details" arrow placement="top">
                                                            <InfoOutlinedIcon sx={{ cursor: 'pointer', fontSize: '18px' }} onClick={(e) => { e.stopPropagation(); handleClosingDetailsClick(i); }} />
                                                        </Tooltip>
                                                    ) : null}
                                                </Box>
                                            </TableCell>
                                        ))}

                                        <TableCell sx={{ ...yearTotalCell, backgroundColor: COLORS.yearTotalClosing, color: '#fff' }} >
                                           {formatCurrencyAmount(closingBalance.year_total, selectedCurrency)}
                                        </TableCell>
                                    </TableRow>

                                    {expandedBalance.closing ? renderBalanceSubRows(closingBreakdown, 'closing') : null}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
        </>
    );
};

export default CashFlowListing;
